import {
  CountriesAlpha2,
  State,
  toCountryLabel,
  toStateLabel
} from '@sequencehq/api/utils/commonEnums'

import { fieldLabel, parseFailingFields } from './integrationSyncErrorMessages'
import {
  RecordAddressLine,
  SyncErrorEntityAddress,
  SyncErrorTaskInput
} from './types'

/**
 * The address slots the card shows, in the order an address reads. `field` is
 * the backend's failing-field path, so an empty slot named there is greyed
 * rather than dropped.
 */
const ADDRESS_SLOTS: readonly {
  field: string
  value: (address: SyncErrorEntityAddress) => string | undefined
}[] = [
  { field: 'address', value: () => undefined },
  { field: 'address.line1', value: address => address.line1 ?? undefined },
  { field: 'address.line2', value: address => address.line2 ?? undefined },
  { field: 'address.city', value: address => address.city ?? undefined },
  {
    field: 'address.state',
    value: address => (address.state ? stateLabel(address.state) : undefined)
  },
  {
    field: 'address.postCode',
    value: address => address.postCode ?? undefined
  },
  {
    field: 'address.country',
    value: address =>
      address.country ? countryLabel(address.country) : undefined
  }
]

/**
 * The record card's address block: every slot the snapshot filled, plus the
 * empty ones the sync blamed, so the operator sees which are still to fill.
 */
export const recordAddressLines = (
  input: SyncErrorTaskInput
): RecordAddressLine[] => {
  const snapshot = input.entitySnapshot

  if (!snapshot || snapshot.snapshotType !== 'CUSTOMER') {
    return []
  }

  const address = snapshot.address
  const failingFieldNames = new Set(
    (parseFailingFields(input.errorMessage) ?? []).map(field => field.name)
  )

  return ADDRESS_SLOTS.reduce<RecordAddressLine[]>((lines, slot) => {
    const value = address ? slot.value(address)?.trim() : undefined

    if (value) {
      return [
        ...lines,
        { field: slot.field, text: value, isPlaceholder: false }
      ]
    }

    if (failingFieldNames.has(slot.field)) {
      return [
        ...lines,
        {
          field: slot.field,
          text: fieldLabel(input, slot.field),
          isPlaceholder: true
        }
      ]
    }

    return lines
  }, [])
}

// The integration passes the country and state through unnormalised when it
// doesn't recognise them, so show what was typed rather than nothing.
const countryLabel = (country: string): string =>
  toCountryLabel(country as CountriesAlpha2) ?? country

const stateLabel = (state: string): string =>
  toStateLabel(state as State) ?? state
